import React, { useEffect, useState } from 'react';

import ALink from '~/components/features/custom-link';

import { useForm, useAuth, useProgram } from 'hooks';
import { removeFirstChar } from '~/utils';
import { useRouter } from 'next/router';

const SignIn = () => {
  const { program } = useProgram();
  const { push, replace, query } = useRouter();
  const { values, onChange } = useForm<{
    username: string;
    password: string;
  }>({
    username: null,
    password: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loadedBackground, setLoadedBackground] = useState<string | null>(null);
  const { logIn, logInUser, isLoggedIn } = useAuth();

  // No entregar la URL al CSS hasta que el navegador haya terminado de cargarla.
  // Esto evita pintar un background incompleto y también evita `url(undefined)` /
  // `url(null)` cuando el programa todavía no tiene un asset válido configurado.
  useEffect(() => {
    const backgroundUrl = program?.loginScreen?.trim();
    setLoadedBackground(null);

    if (!backgroundUrl) return;

    let isCurrent = true;
    const background = new Image();

    background.onload = () => {
      if (isCurrent) setLoadedBackground(backgroundUrl);
    };
    background.onerror = () => {
      // El fondo neutro mantiene usable el login si el asset remoto no responde.
      if (isCurrent) setLoadedBackground(null);
    };
    background.src = backgroundUrl;

    return () => {
      isCurrent = false;
      background.onload = null;
      background.onerror = null;
    };
  }, [program?.loginScreen]);

  // El login normal (desde esta misma página) no navega: `logIn()` solo cambia
  // el estado de auth. Sin esto, tras autenticar el Header ya muestra el nav
  // logueado pero la ruta sigue en /signin renderizando el formulario. Al
  // detectar sesión, redirigimos al destino (respetando `return_to`).
  useEffect(() => {
    if (!isLoggedIn) return;
    const returnTo =
      typeof query.return_to === 'string' && query.return_to.startsWith('/')
        ? query.return_to
        : '/';
    void replace(returnTo);
  }, [isLoggedIn, query.return_to, replace]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.username);

    if (program?.id === 40 && isEmail) {
      try {
        const token = await logInUser(values.username, values.password);
        if (token) {
          push(`https://asesores.conticlubec.com/login?token=${token}`);
        }
      } catch (err) {
        console.error('Error en logInUser', err);
      }
    } else {
      logIn(values.username, values.password);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        // `minHeight` en vez de `height` para que el teclado en móvil no comprima
        // el layout y deje el card visible por encima del keyboard.
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        // `cover` en vez de `100% 100%` para respetar la proporción original
        // del asset y evitar que se deforme en aspect ratios verticales.
        backgroundColor: '#EAEAEA',
        backgroundImage: loadedBackground ? `url(${JSON.stringify(loadedBackground)})` : undefined,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        padding: '16px',
      }}
    >
      <div
        className="form-box"
        style={{
          background: '#EAEAEA',
          padding: '24px',
          borderRadius: '10px',
          // Fluido hasta 380px: en móvil ocupa todo el ancho disponible menos
          // el padding del contenedor; en desktop se queda anclado a 380px.
          width: '100%',
          maxWidth: '380px',
        }}
      >
        <div className="tab tab-nav-simple tab-nav-boxed form-tab">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ALink href="/" className="logo">
              <img src={program.logoBig} alt="logo" width="153" height="44" />
            </ALink>
          </div>
          <span className="nav-link border-no lh-1 ls-normal">Ingresa</span>

          <div className="tab-">
            <form action="#" onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="singin-email"
                  name="singin-email"
                  placeholder="Usuario *"
                  required
                  value={values.username || ''}
                  onChange={(e) =>
                    onChange('username', e.target.value.length > 0 ? e.target.value : null)
                  }
                />
              </div>
              <div className="form-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  id="singin-password"
                  placeholder="Contraseña *"
                  name="singin-password"
                  required
                  value={values.password || ''}
                  onChange={(e) =>
                    onChange('password', e.target.value.length > 0 ? e.target.value : null)
                  }
                />
                <p
                  style={{
                    textAlign: 'right',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                }}
              >
                {(program?.supportPhone?.length ?? 0) > 0 ? (
                  <a
                    href={`https://api.whatsapp.com/send?phone=${removeFirstChar(
                      program?.supportPhone ?? '',
                    )}&text=${encodeURIComponent(
                      `Hola 👋, necesito recuperar mi contraseña para acceder al programa ${program?.name ?? ''}. 
Por favor, ayúdenme con el proceso 🙏.`,
                    )}`}
                    style={{
                      textAlign: 'right',
                      width: '100%',
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                ) : (
                  <a
                    href={`mailto:${program?.supportEmail ?? ''}?subject=${encodeURIComponent(
                      `Recuperación de contraseña - ${program?.name ?? ''}`,
                    )}&body=${encodeURIComponent(
                      `Hola 👋, necesito recuperar mi contraseña para acceder al programa ${program?.name ?? ''}. 
Por favor, ayúdenme con el proceso 🙏.`,
                    )}`}
                    style={{
                      textAlign: 'right',
                      width: '100%',
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                )}
              </div>
              <button className="btn btn-dark btn-block btn-rounded mt-5" type="submit">
                Ingresar
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
